import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Lock, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { IMAGES, NIGERIA_STATES, STATE_CAPITALS, stateHref, stateSlug, waLink } from "@/data/content";
import { Nav } from "@/components/Nav";
import { Seo } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { FadeUp } from "@/components/Reveal";

const Section = ({ title, items, testid }) =>
  items?.length ? (
    <section className="border-t border-jawa-stone py-12" data-testid={testid}>
      <h2 className="font-serif text-3xl font-medium tracking-tight text-jawa-espresso">{title}</h2>
      <ul className="mt-6 grid gap-3 sm:grid-cols-2">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-jawa-espresso/75">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 bg-jawa-gold" />
            {item}
          </li>
        ))}
      </ul>
    </section>
  ) : null;

const STATE_TINTS = ["#2A1F1D", "#0E3B33", "#4A2130", "#3A2C10"];
const tintFor = (slug) => STATE_TINTS[[...slug].reduce((a, c) => a + c.charCodeAt(0), 0) % STATE_TINTS.length];

export default function DestinationPage() {
  const { slug, stateSlugParam } = useParams();
  const isState = Boolean(stateSlugParam);
  const effectiveSlug = isState ? stateSlugParam : slug;
  const [dest, setDest] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const slugToFetch = isState ? `state-${stateSlugParam}` : effectiveSlug;
    api
      .get(`/destinations/${slugToFetch}`)
      .then((res) => setDest(res.data))
      .catch(() => setDest(null))
      .finally(() => setLoaded(true));
  }, [effectiveSlug, isState, stateSlugParam]);

  return (
    <div className="bg-jawa-ivory" data-testid={`destination-page-${effectiveSlug}`}>
      <Nav />
      <Seo
        title={dest ? `${dest.name} — Destination Guide | JÀWÀ Africa` : "Destination Guide | JÀWÀ Africa"}
        description={(dest?.overview || "Curated destination guidance across Nigeria and Africa.").slice(0, 158)}
      />
      {!loaded ? (
        <div className="pt-40 pb-24 text-center text-sm uppercase tracking-[0.25em] text-jawa-espresso/50">Loading…</div>
      ) : !dest ? (
        <div className="mx-auto max-w-3xl px-6 pt-40 pb-24 text-center" data-testid="destination-not-found">
          <h1 className="font-serif text-4xl text-jawa-espresso">Destination not found</h1>
          <Link to="/destinations" className="mt-6 inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-jawa-terracotta">
            <ArrowLeft className="h-4 w-4" /> All destinations
          </Link>
        </div>
      ) : (
        <>
          <header className="relative flex min-h-[60vh] items-end overflow-hidden">
            {dest.image_url ? (
              <>
                <img src={dest.image_url} alt={dest.name} className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-jawa-nearblack/90 via-jawa-nearblack/35 to-jawa-nearblack/25" />
              </>
            ) : (
              <div
                className="absolute inset-0"
                style={{ background: `linear-gradient(150deg, ${tintFor(dest.slug)} 0%, #111111 130%)` }}
              >
                <span className="absolute -right-8 top-1/2 -translate-y-1/2 select-none font-serif text-[26rem] font-medium leading-none text-jawa-ivory/[0.06]">
                  {dest.name[0]}
                </span>
              </div>
            )}
            <div className="relative mx-auto w-full max-w-7xl px-6 pb-14 pt-40 lg:px-10">
              <FadeUp>
                <p className="flex items-center gap-2 text-xs uppercase tracking-[0.35em] text-jawa-gold">
                  <MapPin className="h-3.5 w-3.5" /> {dest.region}
                </p>
                <h1 className="mt-3 font-serif text-5xl font-medium tracking-tight text-jawa-ivory sm:text-6xl">{dest.name}</h1>
                <p className="mt-3 text-sm uppercase tracking-[0.25em] text-jawa-ivory/70">{dest.tagline}</p>
                {STATE_CAPITALS[dest.name] && (
                  <p className="mt-2 text-xs uppercase tracking-[0.2em] text-jawa-ivory/50" data-testid="destination-capital">
                    {dest.name === "Abuja" ? "Federal Capital Territory — not a state" : `Capital: ${STATE_CAPITALS[dest.name]}`}
                  </p>
                )}
              </FadeUp>
            </div>
            {dest.image_url && dest.image_credit && (
              <p className="absolute bottom-3 right-4 text-[9px] uppercase tracking-wider text-jawa-ivory/50" data-testid="destination-image-credit">
                {dest.image_credit}
              </p>
            )}
            {dest.status === "coming_soon" && (
              <div className="absolute inset-0 flex items-center justify-center bg-jawa-ivory/20 backdrop-blur-[2px]">
                <div className="border border-jawa-gold/50 bg-jawa-nearblack/80 px-8 py-6 text-center backdrop-blur-md" data-testid="coming-soon-banner">
                  <Lock className="mx-auto h-5 w-5 text-jawa-gold" />
                  <p className="mt-3 font-serif text-xl text-jawa-ivory">More destination information coming soon</p>
                </div>
              </div>
            )}
          </header>

          <main className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
            <section data-testid="destination-about">
              <h2 className="font-serif text-3xl font-medium tracking-tight text-jawa-espresso">About {dest.name}</h2>
              <p className="mt-5 max-w-3xl text-lg leading-relaxed text-jawa-espresso/85" data-testid="destination-overview">
                {dest.overview}
              </p>
            </section>

            <Section title="Things to do" items={dest.places} testid="destination-places" />
            <Section title="Experiences" items={(dest.experiences || []).filter((x) => !x.startsWith("Food to try"))} testid="destination-experiences" />
            <Section
              title="Food & flavour"
              items={(dest.experiences || [])
                .filter((x) => x.startsWith("Food to try"))
                .map((x) => x.replace(/^Food to try:\s*/, "").replace(/\.$/, ""))}
              testid="destination-food"
            />
            <Section title="Getting there & practical notes" items={dest.practical} testid="destination-practical" />
            <p className="mt-2 max-w-3xl text-[11px] leading-relaxed text-jawa-espresso/45">
              Transport and service details change — always confirm current schedules and travel advice before you travel.
            </p>

            {dest.slug === "nigeria" && (
              <section className="border-t border-jawa-stone py-12" data-testid="nigeria-states-section">
                <h2 className="font-serif text-3xl font-medium tracking-tight text-jawa-espresso">Nigeria, state by state</h2>
                <p className="mt-2 text-xs uppercase tracking-[0.25em] text-jawa-terracotta">36 States + FCT</p>
                <div className="mt-8 flex flex-wrap gap-2.5" data-testid="nigeria-states-list">
                  {NIGERIA_STATES.map((s) => (
                    <Link
                      key={s}
                      to={stateHref(s)}
                      data-testid={`state-link-${stateSlug(s)}`}
                      className={`cursor-pointer border px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors ${
                        s.includes("FCT") || s === "Lagos"
                          ? "border-jawa-gold bg-jawa-gold/10 text-jawa-espresso hover:bg-jawa-gold/25"
                          : "border-jawa-espresso/20 text-jawa-espresso/70 hover:border-jawa-terracotta hover:text-jawa-terracotta"
                      }`}
                    >
                      {s}
                    </Link>
                  ))}
                </div>
              </section>
            )}

            <section className="mt-12 border border-jawa-espresso/15 bg-jawa-espresso p-8 lg:p-12" data-testid="destination-services-cta">
              <h2 className="font-serif text-2xl font-medium text-jawa-ivory">JÀWÀ services for {dest.name}</h2>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-jawa-ivory/70">
                Arrival support, verified stays, drivers, concierge and the full A–Z services directory.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/services"
                  data-testid="destination-services-link"
                  className="flex items-center gap-1.5 bg-jawa-coral px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-jawa-coral/90"
                >
                  Explore Services <ArrowUpRight className="h-4 w-4" />
                </Link>
                <a
                  href={waLink(`Hello JÀWÀ — I'm planning time in ${dest.name}.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="destination-whatsapp-cta"
                  className="border border-jawa-gold px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-gold transition-colors hover:bg-jawa-gold/10"
                >
                  WhatsApp the Concierge
                </a>
              </div>
            </section>
          </main>
        </>
      )}
      <Footer />
    </div>
  );
}
